"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import NextStepCard from "@/app/(components)/generics/cards/NextStepCard/NextStepCard";
import IconGift from "@/app/(components)/generics/Icons/IconGift";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useFetchCallAnalysisOverview } from "@/app/hooks/fetchs/calls/useFetchCallAnalysisOverview";
import { useOfferActions } from "@/app/hooks/useOfferActions";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import {
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_CURSOR,
  CHART_LABEL_STYLE,
  formatDuration,
  OVERVIEW_PLACEHOLDER,
  OVERVIEW_SCORE_LABELS,
  scoreDelta,
  scoreFill,
} from "./AnalysisOverview.logic";
import styles from "./AnalysisOverview.module.css";
import MembersChart from "./MembersChart";
import ScoreTimeline from "./ScoreTimeline/ScoreTimeline";
import ScoreTooltip from "./ScoreTooltip";
import TopicChart from "./TopicChart";
import TypesChart from "./TypesChart";

type Props = DefaultProps & {
  filter?: CallScopeFilter;
  enabled?: boolean;
};

export default function AnalysisOverview(props: Props) {
  const T = useT();
  const { params, filter = {}, enabled = true } = props;
  const project = ProjectStore((state) => state.currentProject);
  const offerActions = useOfferActions(params);

  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const { data: fetched, isLoading } = useFetchCallAnalysisOverview(
    project?.id,
    currentOfferId,
    filter,
    enabled,
  );

  const isPlaceholder = !enabled;
  const overview = isPlaceholder ? OVERVIEW_PLACEHOLDER : fetched;

  if (!isPlaceholder && (isLoading || !overview)) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader color="var(--color-green-3)" delay={Limits.CALL_ANALYSIS_LOADER_DELAY} />
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  if (overview.analysedCalls === 0) {
    return <p className={styles.empty}>{T(TKey.CALL_OVERVIEW_EMPTY)}</p>;
  }

  const delta = scoreDelta(overview.globalScore, overview.previousGlobalScore);

  const scoreRows = overview.scores.map((score) => ({
    label: T(OVERVIEW_SCORE_LABELS[score.key]) as string,
    value: score.value,
  }));

  return (
    <div className={UStyles.classNames(styles.container, isPlaceholder ? styles.blurred : "")}>
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.kpiValue}>
            <span className={styles.kpiRatio}>
              {overview.analysedCalls}
              <span className={styles.kpiTotal}>/{overview.totalCalls}</span>
            </span>
          </span>
          <span className={styles.kpiLabel}>{T(TKey.CALL_OVERVIEW_KPI_CALLS)}</span>
        </div>

        <div className={styles.kpi}>
          <span className={styles.kpiValue}>{formatDuration(overview.durationSeconds)}</span>
          <span className={styles.kpiLabel}>{T(TKey.CALL_OVERVIEW_KPI_DURATION)}</span>
        </div>

        <div className={styles.kpi}>
          <span className={styles.kpiValue}>
            {overview.globalScore ?? "-"}
            {delta !== null && delta !== 0 && (
              <span
                className={`${styles.trend} ${delta > 0 ? styles.trendUp : styles.trendDown}`}
                title={T(TKey.CALL_OVERVIEW_TREND_HINT) as string}
              >
                {delta > 0 ? "+" : ""}
                {delta}
              </span>
            )}
          </span>
          <span className={styles.kpiLabel}>{T(TKey.CALL_OVERVIEW_KPI_SCORE)}</span>
        </div>

        {overview.avgQuestions !== null && (
          <div className={styles.kpi}>
            <span className={styles.kpiValue}>{overview.avgQuestions}</span>
            <span className={styles.kpiLabel}>{T(TKey.CALL_OVERVIEW_KPI_QUESTIONS)}</span>
          </div>
        )}
      </div>

      <div className={styles.grid}>
        {overview.timeline.length > 1 && <ScoreTimeline timeline={overview.timeline} />}

        {scoreRows.length > 0 && (
          <div className={styles.card}>
            <span className={styles.cardTitle}>{T(TKey.CALL_OVERVIEW_SCORES_TITLE)}</span>
            <div className={styles.chart}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreRows} margin={{ top: 16, right: 8, bottom: 0, left: -22 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={CHART_AXIS_TICK}
                    tickLine={false}
                    axisLine={CHART_AXIS_LINE}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={CHART_AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip cursor={CHART_CURSOR} content={ScoreTooltip} />
                  <Bar
                    dataKey="value"
                    name={T(TKey.CALL_OVERVIEW_KPI_SCORE) as string}
                    radius={[4, 4, 0, 0]}
                    barSize={34}
                  >
                    <LabelList dataKey="value" position="top" style={CHART_LABEL_STYLE} />
                    {scoreRows.map((row) => (
                      <Cell key={row.label} className={scoreFill(row.value)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {overview.types.length > 0 && <TypesChart types={overview.types} />}

        {overview.topInsights.length > 0 && (
          <TopicChart
            title={T(TKey.CALL_OVERVIEW_TOP_INSIGHTS_TITLE) as string}
            topics={overview.topInsights}
            total={overview.analysedCalls}
          />
        )}

        {overview.topPatterns.length > 0 && (
          <TopicChart
            title={T(TKey.CALL_OVERVIEW_TOP_PATTERNS_TITLE) as string}
            topics={overview.topPatterns}
            total={overview.analysedCalls}
          />
        )}

        <NextStepCard
          params={params}
          className={styles.nextStep}
          icon={<IconGift />}
          title={T(TKey.CALL_ANALYSIS_NEXT_OFFER_TITLE) as string}
          description={T(TKey.CALL_ANALYSIS_NEXT_OFFER_DESCRIPTION) as string}
          ctaLabel={T(TKey.CALL_ANALYSIS_NEXT_OFFER_CTA) as string}
          onClick={offerActions.add}
        />

        {overview.members.length > 1 && (
          <MembersChart members={overview.members} average={overview.globalScore} />
        )}
      </div>
    </div>
  );
}
